/* eslint-disable */
import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";

import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import { assetImages } from "constants";
import {
  getDateFormatInSpanish,
  mergeListById,
} from "helper/Common/CommonHelper";
import { removeTagHandler } from "helper/TagHelper/TagHelper";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import SaveDiagnosticImagingPopup from "./Popup/SaveDiagnosticImagingPopup";

const DiagnosticImagingSection = ({
  patientObj = null,
  doctorObj = null,
  clinicObj = null,
}) => {
  const params = useParams();
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  const [isLoading, setIsLoading] = useState(false);
  const [imagingList, setImagingList] = useState([]);

  const [rowSelection, setRowSelection] = useState({});
  const [selectedImagingIds, setSelectedImagingIds] = useState([]);
  const [selectedImagingId, setSelectedImagingId] = useState(null);

  //json filter query
  const [jsonFilterQuery, setJsonFilterQuery] = useState([]);
  const [isFilterReset, setIsFilterReset] = useState(false);
  const [reloadData, setReloadData] = useState(false);

  //function for get all diagnostic imaging by patient id
  const getAllDiagnosticImagingByPatientId = async () => {
    try {
      setIsLoading(true);

      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_DIAGNOSTIC_IMAGING +
        `?token=${token}&filterpatientids=${params.id}`;

      console.log("requestUrl in get all diagnostic imaging", requestUrl);

      const response = await getData(requestUrl);

      setIsLoading(false);

      if (response.status) {
        setImagingList(response.data);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  // After any action
  const afterActionClose = (data = []) => {
    setTimeout(() => {
      setImagingList((prevList) => mergeListById(prevList, data)); // Renamed
    }, 1000);
  };

  const resetFilterData = () => {
    setJsonFilterQuery([]);
    setIsFilterReset(true);
    setReloadData(true);
  };

  useEffect(() => {
    if (params.id) {
      getAllDiagnosticImagingByPatientId();
    }
  }, [params.id]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: "Actions",
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            data-bs-toggle="offcanvas"
            data-bs-target="#saveDiagnosticImagingOffCanvas"
            aria-controls="saveDiagnosticImagingOffCanvas"
            onClick={() => {
              setSelectedImagingId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </button>
        ),
        enableColumnActions: false,
        enableColumnDragging: false,
        enableSorting: false,
      },
      {
        header: "Imaging Name",
        accessorKey: "name",
      },
      {
        header: "Diagnostic Imaging Type",
        accessorKey: "type",
      },
      {
        header: "Assigned To",
        accessorKey: "assignedpersonname",
        Cell: ({ row }) => (
          <div
            className={
              row.original.assigntoname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.assigntoimage === ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.assigntoimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.assigntoname}</span>
            </p>
          </div>
        ),
      },
      {
        header: "Doctor Name",
        accessorKey: "doctorname",
        Cell: ({ row }) => (
          <div
            className={
              row.original.doctorname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.doctorimage === ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.doctorimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.doctorname}</span>
            </p>
          </div>
        ),
      },
      {
        header: "Priority",
        accessorKey: "priority",
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.priority === "High"
                ? "bg-danger"
                : row.original.priority === "Medium"
                ? "bg-warning"
                : "bg-info"
            }`}
          >
            {row.original.priority}
          </span>
        ),
      },
      {
        header: "Scheduled Date",
        accessorKey: "scheduledDate",
        Cell: ({ row }) => (
          <div className="joined border-bottom-0">
            <p className="d-flex align-items-center gap-2">
              <span className="d-block material-symbols-outlined icon-md">
                calendar_month
              </span>
              <span className="d-block">
                {new Date(row.original.scheduledDate).toLocaleDateString(
                  "en-GB",
                  {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  }
                )}
              </span>
            </p>
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "diagnosisstatus",
        Cell: ({ row }) => (
          <p
            className={`d-flex align-items-center gap-1 ${row.original.statusColor} fw-semibold`}
          >
            <span className="d-block material-symbols-outlined icon-md">
              check_circle
            </span>
            <span className="d-block">{row.original.diagnosisstatus}</span>
          </p>
        ),
      },
    ],
    []
  );

  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ];

  return (
    <div className="p-4 bg-white h-100 rounded-10 shadow-sm">
      <div className="filter-container mb-4">
        <div className="row align-items-center mb-4">
          <div className="col-lg-4">
            <h3 className="mb-0">Diagnostic Imaging</h3>
          </div>
          <div className="col-lg-8 d-flex align-items-center justify-content-end gap-1 gap-sm-2">
            <Link to="#" className="btn btn-gray" onClick={resetFilterData}>
              <span className="d-block material-symbols-outlined">refresh</span>
            </Link>

            {/* <Link to="#" className="btn btn-gray">
              <span className="d-block material-symbols-outlined">tune</span>
            </Link> */}
            <Link
              to="#"
              className="btn btn-primary"
              data-bs-toggle="offcanvas"
              data-bs-target="#saveDiagnosticImagingOffCanvas"
              aria-controls="saveDiagnosticImagingOffCanvas"
            >
              Add New
            </Link>
          </div>
        </div>
      </div>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <div className="table-wrapper">
          <MaterialReactTable
            columns={columns}
            data={imagingList}
            enableGrouping
            enableRowSelection
            getRowId={(row) => row._id}
            onRowSelectionChange={setRowSelection}
            state={{ rowSelection, columnOrder }}
            initialState={{ rowSelection, columnOrder }}
            defaultColumn={{
              minSize: 20,
              maxSize: 300,
              size: 50,
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "60vh",
              },
            }}
            enableStickyHeader
          />
        </div>
      )}

      <SaveDiagnosticImagingPopup
        selectedImagingId={selectedImagingId}
        setSelectedImagingId={setSelectedImagingId}
        afterActionClose={afterActionClose}
        patientObj={patientObj}
        doctorObj={doctorObj}
        clinicObj={clinicObj}
      />
    </div>
  );
};

export default DiagnosticImagingSection;
